import React, { useEffect, useState } from 'react';
import { Check, Info, AlertTriangle, X } from '../../icons';
import { cn } from '../ui/cn';

const STYLES = {
  success: { ring: 'border-accent/40', icon: 'bg-accent-soft text-accent', Icon: Check },
  error: { ring: 'border-danger/40', icon: 'bg-danger-soft text-danger', Icon: AlertTriangle },
  warning: { ring: 'border-amber/40', icon: 'bg-amber-soft text-amber', Icon: AlertTriangle },
  info: { ring: 'border-info/40', icon: 'bg-info-soft text-info', Icon: Info },
};

/**
 * Single toast. Positioned by the parent stack (see ToastStack).
 */
const Toast = ({ message, type = 'info', duration = 3500, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 250);
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const style = STYLES[type] || STYLES.info;
  const { Icon } = style;

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto flex items-center gap-3 w-full max-w-sm bg-card border rounded-xl shadow-pop px-3.5 py-3 transition-all duration-250',
        style.ring,
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      )}
    >
      <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', style.icon)}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 text-sm text-primary leading-snug">{message}</span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => {
          setIsVisible(false);
          setTimeout(onClose, 250);
        }}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-secondary hover:text-primary hover:bg-white/5"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

/** Fixed top-centre stack; shows at most `max` toasts (newest last). */
export const ToastStack = ({ toasts, onRemove, max = 3 }) => {
  const shown = toasts.slice(-max);
  if (shown.length === 0) return null;
  return (
    <div className="pointer-events-none fixed top-3 left-0 right-0 z-[130] flex flex-col items-center gap-2 px-4 pt-safe lg:left-auto lg:right-6 lg:items-end">
      {shown.map((t) => (
        <Toast key={t.id} message={t.message} type={t.type} onClose={() => onRemove(t.id)} />
      ))}
    </div>
  );
};

export default Toast;
